import React, { useState, useEffect } from 'react';
import { Modal, Button, Space, Select, message, Typography, List, Tag, Tooltip } from 'antd';
import { TeamOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import { SpaceUserGroup, SpaceUserGroupMember } from '../../../../../../../types/spaceUserGroups';
import { AkukoAPIService } from '../../../../../../../services/serviceClass';
import { IDENTITY_API } from '../../../../../../../configs/env';
import { PostFolderService } from '../../../../../../../services/PostFolderService';
import { FolderUser, FolderGroup } from '../../../../../../../types/postFolders';
import './style.css';

const { Text } = Typography;
const { confirm } = Modal;

interface PermissionDisplay {
  id: string;
  email?: string;
  name?: string;
  type: 'user' | 'group';
  role: 'admin' | 'edit' | 'view';
  image?: string;
  space_admin?: boolean;
}

interface Props {
  open: boolean;
  spaceId: string;
  folderId: string;
  folderName: string;
  readOnly?: boolean;
  onCancel: () => void;
}

const FolderPermissionsModal: React.FC<Props> = ({
  open,
  spaceId,
  folderId,
  folderName,
  readOnly = false,
  onCancel,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<PermissionDisplay[]>([]);
  const [availableUsers, setAvailableUsers] = useState<SpaceUserGroupMember[]>([]);
  const [availableGroups, setAvailableGroups] = useState<SpaceUserGroup[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<'admin' | 'edit' | 'view'>('view');

  const loadAvailableUsers = async () => {
    try {
      setLoading(true);
      const getSpaceService = new AkukoAPIService(IDENTITY_API, 'space');
      const space = await getSpaceService.read(spaceId) as { account_id: string };
      const getUsersService = new AkukoAPIService(IDENTITY_API, `user/space/${space.account_id}/${spaceId}`);
      const users = await getUsersService.list() as Array<{ uuid: string; email: string; name: string; image: string }>;
      
      // Filter out users who already have access
      const existingUserIds = new Set(permissions.filter(p => p.type === 'user').map(p => p.id));
      const filteredUsers = users.filter(user => !existingUserIds.has(user.uuid));

      setAvailableUsers(filteredUsers.map(user => ({
        id: user.uuid,
        email: user.email,
        name: user?.name,
        image: user?.image,
      })));
    } catch (error: any) {
      message.error(error?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableGroups = async () => {
    try {
      setLoading(true);
      const service = new AkukoAPIService(IDENTITY_API, `space/${spaceId}/user/groups/list`);
      const groups = await service.list() as SpaceUserGroup[];
      
      // Filter out groups that already have access
      const existingGroupIds = new Set(permissions.filter(p => p.type === 'group').map(p => p.id));
      const filteredGroups = groups.filter(group => !existingGroupIds.has(group.id));
      
      setAvailableGroups(filteredGroups);
    } catch (error: any) {
      message.error(error?.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const service = new PostFolderService(`space/${spaceId}/posts/folder`);
      const folder = await service.getFolderDetails(folderId);
      
      const permissions: PermissionDisplay[] = [
        ...folder.metadata.users.map((user: FolderUser): PermissionDisplay => ({
          id: user.id,
          email: user.email,
          name: user?.name,
          image: user?.image,
          type: 'user',
          role: user.role,
          space_admin: user?.space_admin,
        })),
        ...folder.metadata.groups.map((group: FolderGroup): PermissionDisplay => ({
          id: group.id,
          name: group.name,
          type: 'group',
          role: group.role,
        })),
      ];
      setPermissions(permissions);
    } catch (error: any) {
      message.error(error?.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedEntity(null);
      setSelectedRole('view');
      loadPermissions();
    }
  }, [open, folderId, spaceId]);

  useEffect(() => {
    if (open) {
      if (permissions.length > 0) {
        loadAvailableUsers();
        loadAvailableGroups();
      }
    }
  }, [open, folderId, spaceId, permissions]);

  const handleUpdateRole = async (entityId: string, newRole: 'admin' | 'edit' | 'view', type: 'user' | 'group') => {
    try {
      setLoading(true);
      const service = new PostFolderService(`space/${spaceId}/posts/folder/${folderId}/assign/${type}`);
      if (type === 'user') {
        await service.assignUser({
          user_id: entityId,
          role: newRole,
        });
      } else {
        await service.assignGroup({
          group_id: entityId,
          role: newRole,
        });
      }
      await loadPermissions();
      message.success(`${type === 'user' ? 'User' : 'Group'} role updated successfully`, 2);
    } catch (error: any) {
      message.error(error?.message || `Failed to update ${type} role`);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignNew = async () => {
    if (!selectedEntity || !selectedRole) return;

    try {
      setLoading(true);
      const isUser = availableUsers.some(u => u.id === selectedEntity);
      const service = new PostFolderService(`space/${spaceId}/posts/folder/${folderId}/assign/${isUser ? 'user' : 'group'}`);
      if (isUser) {
        await service.assignUser({
          user_id: selectedEntity,
          role: selectedRole,
        });
      } else {
        await service.assignGroup({
          group_id: selectedEntity,
          role: selectedRole,
        });
      }
      await loadPermissions();
      message.success('Access granted successfully', 2);
      setSelectedEntity(null);
    } catch (error: any) {
      message.error(error?.message || 'Failed to grant access');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (permission: PermissionDisplay) => {
    try {
      setLoading(true);
      const service = new PostFolderService(`space/${spaceId}/posts/folder/${folderId}/remove/${permission.type}/${permission.id}`);
      if (permission.type === 'user') {
        await service.removeUser();
      } else {
        await service.removeGroup();
      }
      await loadPermissions();
      message.success(`${permission.type === 'user' ? 'User' : 'Group'} removed successfully`, 2);
    } catch (error: any) {
      message.error(error?.message || `Failed to remove ${permission.type}`);
    } finally {
      setLoading(false);
    }
  };

  const showRemoveConfirm = (permission: PermissionDisplay) => {
    const entityName = permission.type === 'user' 
      ? (permission.name || permission.email) 
      : permission.name;

    confirm({
      title: `Remove ${permission.type === 'user' ? 'user' : 'group'}`,
      content: `Are you sure you want to remove ${entityName}?`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => handleRemove(permission),
      centered: true,
      maskClosable: true,
    });
  };

  const renderAvatar = (image?: string, name?: string, email?: string) => {
    if (image && image.trim() !== '') {
      return (
        <img 
          src={`https://assets.akuko.io/${image}`}
          alt={name || email}
          style={{ width: 32, height: 32, borderRadius: '50%' }}
        />
      );
    }
    
    // Get first letter from name or email
    const displayName = name || email || '';
    const firstLetter = displayName.charAt(0).toUpperCase();
    
    return (
      <div style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: '#5C6BC0', // Indigo color
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '14px',
        fontWeight: 500
      }}>
        {firstLetter}
      </div>
    );
  };

  const renderRoleSelect = (permission: PermissionDisplay) => (
    <Select
      value={permission.role}
      onChange={(newRole) => {
        if (permission.space_admin) {
          message.error('Space admin cannot be changed');
          return;
        }
        handleUpdateRole(permission.id, newRole, permission.type);
      }}
      allowClear
      style={{ width: 100 }}
      disabled={readOnly || permission.space_admin}
    >
      <Select.Option value="admin">Admin</Select.Option>
      <Select.Option value="edit">Editor</Select.Option>
      <Select.Option value="view">Viewer</Select.Option>
    </Select>
  );

  const handleCopyLink = () => {
    const url = `${window.location.origin}/space/${spaceId}/folder/${folderId}`;
    navigator.clipboard.writeText(url).then(() => {
      message.success('Link copied to clipboard');
    }).catch(() => {
      message.error('Failed to copy link');
    });
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <TeamOutlined style={{ marginRight: 8 }} />
            <span>
              {readOnly ? 'View' : 'Share'} {folderName && <span className="folder-name">"{folderName}"</span>}
            </span>
          </div>
          <Tooltip title="Copy folder link">
            <Button
              icon={<LinkOutlined />}
              onClick={handleCopyLink}
              type="text"
              size="middle"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#1890ff',
                padding: '4px 12px',
                borderRadius: '4px',
                transition: 'all 0.3s',
              }}
              className="copy-link-button"
            >
              Copy link
            </Button>
          </Tooltip>
        </div>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={610}
      className="folder-permissions-modal"
      centered
      maskClosable
    >
      {!readOnly && (
        <div style={{ padding: '0 0 20px' }}>
          <Space.Compact style={{ width: '100%' }}>
            <Select
              showSearch
              allowClear
              placeholder="Add people or groups"
              value={selectedEntity}
              onChange={setSelectedEntity}
              style={{ width: '100%' }}
              optionFilterProp="label"
              filterOption={(input, option) => {
                const label = option?.label?.toString().toLowerCase() || '';
                const value = option?.value?.toString().toLowerCase() || '';
                const searchTerm = input.toLowerCase();
                
                // For users, also search in email
                const user = availableUsers.find(u => u.id === option?.value);
                if (user) {
                  return label.includes(searchTerm) || 
                         user.email.toLowerCase().includes(searchTerm);
                }
                
                // For groups, search in name
                return label.includes(searchTerm) || value.includes(searchTerm);
              }}
              notFoundContent="No users or groups found"
              dropdownStyle={{ padding: '8px 0' }}
              optionLabelProp="label"
            >
              {availableUsers.map(user => (
                <Select.Option 
                  key={user.id} 
                  value={user.id}
                  label={user.name || user.email.split('@')[0]}
                >
                  <div style={{ padding: '4px 0' }}>
                    <Space align="center" size={12}>
                      {renderAvatar(user.image, user.name, user.email)}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Text strong>{user.name || user.email.split('@')[0]}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>{user.email}</Text>
                      </div>
                    </Space>
                  </div>
                </Select.Option>
              ))}
              {availableGroups.map(group => (
                <Select.Option 
                  key={group.id} 
                  value={group.id}
                  label={group.name}
                >
                  <div style={{ padding: '4px 0' }}>
                    <Space align="center" size={12}>
                      <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <TeamOutlined style={{ color: 'white', fontSize: '18px' }} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Text strong>{group.name}</Text>
                        <Text type="secondary" style={{ fontSize: '12px' }}>Group</Text>
                      </div>
                    </Space>
                  </div>
                </Select.Option>
              ))}
            </Select>
            <Select
              value={selectedRole}
              onChange={setSelectedRole}
              allowClear
              style={{ width: 120 }}
            >
              <Select.Option value="admin">Admin</Select.Option>
              <Select.Option value="edit">Editor</Select.Option>
              <Select.Option value="view">Viewer</Select.Option>
            </Select>
            <Button 
              type="primary"
              onClick={handleAssignNew}
              disabled={!selectedEntity || loading}
            >
              Add
            </Button>
          </Space.Compact>
        </div>
      )}

      <div style={{ marginTop: !readOnly ? 16 : 0 }}>
        <Text strong style={{ fontSize: 14 }}>People with access</Text>
        <List
          dataSource={permissions}
          loading={loading}
          renderItem={permission => (
            <List.Item
              key={permission.id}
              style={{ padding: '12px 16px' }}
              actions={!readOnly ? [
                <Space>
                  {renderRoleSelect(permission)}
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    danger={!permission.space_admin}
                    disabled={permission.space_admin}
                    onClick={() => {
                      if (permission.space_admin) {
                        message.error('Space admin cannot be removed');
                        return;
                      }
                      showRemoveConfirm(permission);
                    }}
                  />
                </Space>
              ] : [
                <Space>
                  <Tag color={
                    permission.role === 'admin' ? 'blue' : 
                    permission.role === 'edit' ? 'green' : 
                    'default'
                  }>
                    {permission.role === 'admin' ? 'Admin' :
                     permission.role === 'edit' ? 'Editor' :
                     'Viewer'}
                  </Tag>
                </Space>
              ]}
            >
              <List.Item.Meta
                avatar={
                  permission.type === 'user' 
                    ? renderAvatar(permission.image, permission.name, permission.email)
                    : (
                      <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <TeamOutlined style={{ color: 'white', fontSize: '18px' }} />
                      </div>
                    )
                }
                title={
                  <>
                    {permission.name || permission.email?.split('@')[0]}
                    {permission.space_admin && <Tag color="blue" style={{ marginLeft: 8 }}>Space Admin</Tag>}
                  </>
                }
                description={permission.type === 'group' ? 'Group' : permission.email}
              />
            </List.Item>
          )}
        />
      </div>
    </Modal>
  );
};

export default FolderPermissionsModal; 